import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme';
import { FilterBox } from './FilterBox';
import { CampaignFactory, ParameterFactory, StationFactory } from '../../resource';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Form } from '@independent-software/typeui/controls/Form';
import { Input } from '@independent-software/typeui/controls/Input';
import { Export } from '../../modules';

interface IProps {
  /** @ignore */
  className?: string;

  parameters: number[];
  stations: number[];
  campaigns: number[];
  dateMin: string;
  dateMax: string;
  valueMin: string;
  valueMax: string;
  depthMin: string;
  depthMax: string;

  onChange: (
    parameters: number[], stations: number[], campaigns: number[],
    dateMin: string, dateMax: string,
    valueMin: string, valueMax: string,
    depthMin: string, depthMax: string
  ) => void;

  onExport: (format: string) => void;
}

const FilterPanelBase = (props: IProps) => {
  const [expanded, setExpanded] = React.useState(false);
  
  const handleChangeParameters = (parameters: number[]) => {
    props.onChange(parameters, props.stations, props.campaigns, props.dateMin, props.dateMax, props.valueMin, props.valueMax, props.depthMin, props.depthMax);
  }

  const handleChangeStations = (stations: number[]) => {
    props.onChange(props.parameters, stations, props.campaigns, props.dateMin, props.dateMax, props.valueMin, props.valueMax, props.depthMin, props.depthMax);
  }

  const handleChangeCampaigns = (campaigns: number[]) => {
    props.onChange(props.parameters, props.stations, campaigns, props.dateMin, props.dateMax, props.valueMin, props.valueMax, props.depthMin, props.depthMax);
  }

  const handleChangeFromDate = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, value, props.dateMax, props.valueMin, props.valueMax, props.depthMin, props.depthMax);
  }

  const handleChangeToDate = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, props.dateMin, value, props.valueMin, props.valueMax, props.depthMin, props.depthMax);
  }

  const handleChangeFromValue = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, props.dateMin, props.dateMax, value, props.valueMax, props.depthMin, props.depthMax);
  }  

  const handleChangeToValue = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, props.dateMin, props.dateMax, props.valueMin, value, props.depthMin, props.depthMax);
  }  

  const handleChangeFromDepth = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, props.dateMin, props.dateMax, props.valueMin, props.valueMax, value, props.depthMax);
  }  

  const handleChangeToDepth = (value: string) => {
    props.onChange(props.parameters, props.stations, props.campaigns, props.dateMin, props.dateMax, props.valueMin, props.valueMax, props.depthMin, value);
  }  

  return (
    <div className={props.className} style={{height: `${expanded ? '500px' : '304px'}`}}>
      <Toggle>
        <Icon name="chevron" rotated={expanded ? 90 : -90} onClick={() => setExpanded(!expanded)}/>
      </Toggle>
      <FilterBox label="Parameters" factory={ParameterFactory} selectedItems={props.parameters} onChange={handleChangeParameters}/>
      <FilterBox label="Stations" factory={StationFactory} selectedItems={props.stations} onChange={handleChangeStations}/>
      <FilterBox label="Campaigns" factory={CampaignFactory} selectedItems={props.campaigns} onChange={handleChangeCampaigns}/>
      <div style={{flex: 1}}>
        <Title>&nbsp;</Title>
        <Flex.Columns count={2}>
          <Form.Uncontrolled hint="Filter by start date">
            <Input name="from" type="date" fluid clearable value={props.dateMin} onChange={handleChangeFromDate}/>
          </Form.Uncontrolled>
          <Form.Uncontrolled hint="Filter by end date">
            <Input name="to" type="date" fluid clearable value={props.dateMax} onChange={handleChangeToDate}/>
          </Form.Uncontrolled>
        </Flex.Columns>
        <Flex.Columns count={2}>
          <Form.Uncontrolled hint="Minimum value">
            <Input name="fromvalue" type="text" fluid clearable value={props.valueMin} onChange={handleChangeFromValue}/>
          </Form.Uncontrolled>
          <Form.Uncontrolled hint="Maximum value">
            <Input name="tovalue" type="text" fluid clearable value={props.valueMax} onChange={handleChangeToValue}/>
          </Form.Uncontrolled>    
        </Flex.Columns>              
        <Flex.Columns count={2}>  
          <Form.Uncontrolled hint="Minimum depth">
            <Input name="fromdepth" type="text" fluid clearable value={props.depthMin} onChange={handleChangeFromDepth}/>
          </Form.Uncontrolled>
          <Form.Uncontrolled hint="Maximum depth">
            <Input name="todepth" type="text" fluid clearable value={props.depthMax} onChange={handleChangeToDepth}/>
          </Form.Uncontrolled>    
        </Flex.Columns>
        <div style={{position: 'relative', display: 'flex', justifyContent: 'end'}}>
          <Export onExport={props.onExport}/>        
        </div>
      </div>
    </div>
  )
}

const Title = styled.div`
  font-size: 80%;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`

const Toggle = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

const FilterPanelStyled = styled(FilterPanelBase)`
  position: relative;
  transition: height ease-in-out ${p => p.theme.transition.duration}s;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  border-top: solid 1px ${p => p.theme.normalColor};
  box-shadow: 0 -1px 6px rgba(0,0,0,0.2);
`

const FilterPanel = (props: IProps) => <FilterPanelStyled {...props}/>

export { FilterPanel }
